import React, { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlinePlus, HiOutlineChevronRight, HiOutlineUserGroup, HiOutlineOfficeBuilding } from "react-icons/hi";
import { PiClockCounterClockwise } from "react-icons/pi";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import API from "../../api";
import { capture } from "../../instrument";
import { readListState, writeListState } from "../../utils";
import PageLoadingSpinner from "../../components/PageLoadingSpinner";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useHotkeys } from "react-hotkeys-hook";
import useListShortcuts from "../../hooks/useListShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import { rankBySearch } from "../../utils/search";
import useSavedViews from "../../hooks/useSavedViews";
import useSearchFilters, { SEARCH_OPERATOR } from "../../hooks/useSearchFilters";
import SearchFilters from "../../components/SearchFilters";
import SaveViewModal from "../../components/SaveViewModal";
import ViewTabs from "../../components/ViewTabs";

const DEFAULT_VIEWS = [{ _id: "ALL", name: "Tous", filters: [] }];

const STORAGE_KEY = "clinic-list-state";

const Cabinet = () => {
  const history = useHistory();
  const savedState = useMemo(() => readListState(STORAGE_KEY), []);
  const [search, setSearch] = useState(savedState?.search || "");

  const {
    data: clinics = [],
    isLoading: isLoadingClinics,
    isError: isErrorClinics,
  } = useQuery({
    queryKey: ["clinics"],
    queryFn: async () => {
      const response = await API.get("/clinic");
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
  });

  const { data: laboratories = [], isLoading: isLoadingLaboratories } = useQuery({
    queryKey: ["laboratories"],
    queryFn: async () => {
      const response = await API.get("/admin/laboratory");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });

  const isLoadingData = isLoadingClinics || isLoadingLaboratories;

  const labNameById = useMemo(() => {
    const map = {};
    for (const lab of laboratories) {
      map[lab._id] = lab.name;
    }
    return map;
  }, [laboratories]);

  const filterFields = useMemo(() => {
    const uniqueStatuses = [...new Set(clinics.map((c) => c.status).filter(Boolean))].sort();
    const uniqueLabs = [...new Set(clinics.map((c) => labNameById[c.laboratoryId || c.laboratory]).filter(Boolean))].sort();

    return [
      { key: "status", label: "Statut", operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT], values: uniqueStatuses },
      {
        key: "laboratory",
        label: "Laboratoire",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: uniqueLabs,
        resolve: (clinic) => labNameById[clinic.laboratoryId || clinic.laboratory] || "",
      },
    ];
  }, [clinics, labNameById]);

  const { filters: activeFilters, setFilters: setActiveFilters, applyFilters } = useSearchFilters(filterFields, { initialFilters: savedState?.filters });

  const onFiltersChange = (nextFilters) => {
    setActiveFilters(nextFilters);
    resetSelection();
  };

  const { savedViews, activeViewId, saveModalOpen, setSaveModalOpen, handleSelectView, isViewActive, handleConfirmSaveView, handleDeleteView, saveOptions } = useSavedViews({
    collectionName: "clinic",
    defaultViews: DEFAULT_VIEWS,
    activeFilters,
    setActiveFilters: onFiltersChange,
    initialViewId: savedState?.viewId,
  });

  useEffect(() => {
    writeListState(STORAGE_KEY, { search, filters: activeFilters, viewId: activeViewId });
  }, [search, activeFilters, activeViewId]);

  const getViewBadge = (tab) => {
    if (tab._id === "ALL") return <span className="ml-1.5 text-xs text-gray-400">{clinics.length}</span>;
    return null;
  };

  const getLabName = (clinic) => {
    const labId = clinic.laboratoryId || clinic.laboratory;
    return labNameById[labId] || "—";
  };

  const filteredClinics = useMemo(() => {
    const result = applyFilters(clinics);
    return rankBySearch(result, search, (clinic) => [clinic.name, getLabName(clinic)].filter(Boolean).join(" "));
  }, [clinics, search, activeFilters, labNameById]);

  const listRef = useRef(null);
  const selectedIndexRef = useRef(savedState?.selectedIndex || 0);
  const [selectedIndex, setSelectedIndex] = useState(savedState?.selectedIndex || 0);

  const virtualizer = useVirtualizer({
    count: filteredClinics.length,
    getScrollElement: () => listRef.current,
    initialOffset: savedState?.scrollTop || 0,
    estimateSize: () => 53,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  const resetSelection = () => {
    selectedIndexRef.current = 0;
    setSelectedIndex(0);
    virtualizer.scrollToIndex(0);
    writeListState(STORAGE_KEY, { scrollTop: 0, selectedIndex: 0 });
  };

  const handleNavigateDown = () => {
    const next = selectedIndexRef.current >= filteredClinics.length - 1 ? 0 : selectedIndexRef.current + 1;
    selectedIndexRef.current = next;
    setSelectedIndex(next);
    virtualizer.scrollToIndex(next);
  };

  const handleNavigateUp = () => {
    const prev = selectedIndexRef.current <= 0 ? filteredClinics.length - 1 : selectedIndexRef.current - 1;
    selectedIndexRef.current = prev;
    setSelectedIndex(prev);
    virtualizer.scrollToIndex(prev);
  };

  const onRowMouseMove = (event, index) => {
    const pointerDidNotMove = event.movementX === 0 && event.movementY === 0;
    if (pointerDidNotMove || selectedIndexRef.current === index) return;
    selectedIndexRef.current = index;
    setSelectedIndex(index);
  };

  useListShortcuts(
    {
      onUp: handleNavigateUp,
      onDown: handleNavigateDown,
      onOpen: ({ newTab } = {}) => {
        const clinic = filteredClinics[selectedIndexRef.current];
        if (!clinic) return;
        if (newTab) window.open(`/clinic/${clinic._id}/edit`, "_blank");
        else navigateToClinic(clinic);
      },
    },
    [filteredClinics, virtualizer],
  );

  useHotkeys(SHORTCUTS.CREATE.keys, () => history.push("/clinic/add"), { preventDefault: true });

  const navigateToClinic = (clinic) => {
    const ids = filteredClinics.map((c) => c._id);
    writeListState(STORAGE_KEY, { scrollTop: listRef.current.scrollTop, selectedIndex: ids.indexOf(clinic._id) });
    history.push({ pathname: `/clinic/${clinic._id}/edit`, state: { filteredIds: ids } });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    resetSelection();
  };

  if (isLoadingData) {
    return <PageLoadingSpinner />;
  }

  if (isErrorClinics) {
    return (
      <div className="flex min-h-[calc(100vh-60px)] flex-col items-center justify-center gap-3 bg-gray-50">
        <span className="text-sm text-red-500">Erreur lors du chargement des cliniques</span>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-semibold text-gray-900">Cliniques</h1>
          <ViewTabs
            defaultViews={DEFAULT_VIEWS}
            savedViews={savedViews}
            isViewActive={isViewActive}
            onSelectView={handleSelectView}
            onDeleteView={handleDeleteView}
            getViewBadge={getViewBadge}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
            onClick={() => window.open("/audit-log/clinics", "_blank", "noopener,noreferrer,width=1000,height=800")}
            title="Journal d'audit"
          >
            <PiClockCounterClockwise className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={() => {
              history.push("/clinic/add");
              notifyShortcut("CREATE");
            }}
          >
            <HiOutlinePlus className="h-4 w-4" />
            <span>Nouvelle clinique</span>
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <SearchFilters
          fields={filterFields}
          filters={activeFilters}
          onFiltersChange={onFiltersChange}
          search={search}
          onSearchChange={handleSearchChange}
          placeholder="Rechercher une clinique..."
          saveOptions={saveOptions}
        />

        <div ref={listRef} className="max-h-[calc(100vh-252px)] overflow-y-auto">
          {filteredClinics.length > 0 && (
            <div style={{ height: totalHeight, position: "relative" }}>
              {virtualItems.map((virtualRow) => {
                const clinic = filteredClinics[virtualRow.index];
                return (
                  <div
                    key={clinic._id}
                    data-active={virtualRow.index === selectedIndex || undefined}
                    onMouseMove={(event) => onRowMouseMove(event, virtualRow.index)}
                    style={{ position: "absolute", top: 0, left: 0, right: 0, height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                    className="flex cursor-pointer items-center border-b border-gray-100 px-4 hover:bg-gray-50 data-[active=true]:bg-primary-50/50"
                    onClick={() => navigateToClinic(clinic)}
                  >
                    <span className="min-w-0 truncate text-sm font-medium text-gray-900">{clinic.name}</span>

                    <div className="ml-auto flex flex-shrink-0 items-center gap-4 pl-4">
                      <span className="hidden items-center gap-1.5 text-xs text-gray-500 sm:flex">
                        <HiOutlineOfficeBuilding className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="max-w-[140px] truncate">{getLabName(clinic)}</span>
                      </span>
                      <span className="hidden text-xs tabular-nums text-gray-400 md:block">
                        {clinic.clinicianCount || 0} praticien{(clinic.clinicianCount || 0) !== 1 ? "s" : ""}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${clinic.status === "active" ? "bg-green-500" : "bg-gray-300"}`} />
                        <span className={`text-xs ${clinic.status === "active" ? "text-gray-600" : "text-gray-400"}`}>{clinic.status === "active" ? "Actif" : "Inactif"}</span>
                      </span>
                      <HiOutlineChevronRight className="h-4 w-4 text-gray-300" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filteredClinics.length === 0 && clinics.length > 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <p className="text-sm">
                {search ? `Aucun résultat pour "${search}"` : "Aucun résultat pour les filtres appliqués"}
              </p>
            </div>
          )}

          {clinics.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <HiOutlineUserGroup className="mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm">Aucune clinique</p>
              <p className="mt-1 text-xs">Créez votre première clinique pour commencer</p>
            </div>
          )}
        </div>
      </div>

      {saveModalOpen && (
        <SaveViewModal
          onClose={() => setSaveModalOpen(false)}
          onConfirm={(name) => {
            setSaveModalOpen(false);
            handleConfirmSaveView(name);
          }}
        />
      )}
    </div>
  );
};

export default Cabinet;
